/**
 * Configurator Summary — Sticky bottom bar with delivery info, price,
 * ATC button, payment icons, and expandable addon chips.
 * Shows when the price block scrolls out of view.
 */

if (!customElements.get('configurator-summary')) {
  class ConfiguratorSummary extends HTMLElement {
    constructor() {
      super();
      this.chipsContainer = this.querySelector('.cfg-summary__chips');
      this.totalPriceEl = this.querySelector('.cfg-summary__total-price');
      this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
      this.formId = this.dataset.formId || '';
      this.isVisible = false;
      this.currentSelections = {};

      // Bind methods
      this.onStateChange = this.onStateChange.bind(this);
      this.onAtcClick = this.onAtcClick.bind(this);
      this.onChipsToggle = this.onChipsToggle.bind(this);
    }

    connectedCallback() {
      document.addEventListener('configurator:state-change', this.onStateChange);

      // ATC button
      this.atcBtn = this.querySelector('[data-summary-atc]');
      if (this.atcBtn) {
        this.atcBtn.addEventListener('click', this.onAtcClick);
      }

      // Chips toggle
      this.chipsToggle = this.querySelector('[data-chips-toggle]');
      this.chipsDrawer = this.querySelector('[data-chips-drawer]');
      this.chipsLabel = this.querySelector('[data-chips-label]');
      this.chipsSection = this.querySelector('[data-chips-section]');
      if (this.chipsToggle) {
        this.chipsToggle.addEventListener('click', this.onChipsToggle);
      }

      this.show();
    }

    disconnectedCallback() {
      document.removeEventListener('configurator:state-change', this.onStateChange);
      if (this.atcBtn) {
        this.atcBtn.removeEventListener('click', this.onAtcClick);
      }
      if (this.chipsToggle) {
        this.chipsToggle.removeEventListener('click', this.onChipsToggle);
      }
    }

    show() {
      if (this.isVisible) return;
      this.isVisible = true;
      this.classList.add('cfg-summary--visible');
      this.setAttribute('aria-hidden', 'false');
    }

    hide() {
      if (!this.isVisible) return;
      this.isVisible = false;
      this.classList.remove('cfg-summary--visible');
      this.setAttribute('aria-hidden', 'true');
    }

    /**
     * ATC click — submit the real product form.
     * addon-configurator.js intercepts product-form:cart:request as usual.
     */
    onAtcClick() {
      if (!this.formId) return;
      const form = document.getElementById(this.formId);
      if (form) {
        form.requestSubmit();
      }
    }

    /**
     * Toggle the expandable chips drawer.
     */
    onChipsToggle() {
      if (!this.chipsToggle || !this.chipsDrawer) return;
      const expanded = this.chipsToggle.getAttribute('aria-expanded') === 'true';
      const nowExpanded = !expanded;

      this.chipsToggle.setAttribute('aria-expanded', String(nowExpanded));
      this.chipsDrawer.setAttribute('aria-hidden', String(!nowExpanded));

      if (this.chipsLabel) {
        this.chipsLabel.textContent = nowExpanded ? 'Hide selections' : 'Show selections';
      }
    }

    onStateChange(event) {
      const { selections, grandTotal, basePrice } = event.detail;

      // Update base price if variant changed
      this.basePrice = basePrice;
      this.dataset.basePrice = basePrice;

      // Store current selections
      this.currentSelections = selections;

      // Update total price display
      this.updateTotalPrice(grandTotal);

      // Update selection chips
      this.updateChips(selections);

      // Update chips section visibility
      this.updateChipsVisibility();
    }

    updateTotalPrice(total) {
      if (!this.totalPriceEl) return;

      if (window.theme && window.theme.moneyFormat) {
        this.totalPriceEl.textContent = this.formatMoney(total, window.theme.moneyFormat);
      } else {
        const configurator = document.querySelector('addon-configurator');
        const currency = (configurator && configurator.dataset.currency) || 'USD';
        this.totalPriceEl.textContent = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency
        }).format(total / 100);
      }
    }

    updateChips(selections) {
      if (!this.chipsContainer) return;

      const chips = Object.values(selections).map((sel) => {
        const chip = document.createElement('span');
        chip.className = 'cfg-summary__chip';
        chip.textContent = sel.type === 'text_input'
          ? `${sel.groupLabel}: "${sel.text}"`
          : sel.label;
        return chip;
      });

      this.chipsContainer.replaceChildren(...chips);
    }

    /**
     * Show/hide the chips section based on whether addons are selected.
     */
    updateChipsVisibility() {
      if (!this.chipsSection) return;
      const hasSelections = this.currentSelections && Object.keys(this.currentSelections).length > 0;
      if (hasSelections) {
        this.chipsSection.removeAttribute('data-empty');
      } else {
        this.chipsSection.setAttribute('data-empty', '');
      }
    }

    /**
     * Format money using Shopify's money_format pattern.
     * Handles {{ amount }}, {{ amount_no_decimals }}, {{ amount_with_comma_separator }}, etc.
     */
    formatMoney(cents, format) {
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.round(cents / 100).toString();
      const parts = amount.split('.');
      const amountWithComma = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];

      let result = format;
      result = result.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma);
      result = result.replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amountWithComma.replace(/,/g, "'"));
      result = result.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
      result = result.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals);
      result = result.replace(/\{\{\s*amount\s*\}\}/g, amountWithComma);
      return result;
    }
  }

  customElements.define('configurator-summary', ConfiguratorSummary);
}
